import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { PALISIS_URL, PHONE, PHONE_HREF, parkOpenStatus } from "@/lib/site";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Prices", to: "/pricing" },
  { label: "Times", to: "/times" },
  { label: "Events", to: "/events" },
  { label: "Menu", to: "/menu" },
  { label: "Facilities", to: "/facilities" },
  { label: "Directions", to: "/directions" },
  { label: "Safety", to: "/safety" },
  { label: "Gallery", to: "/gallery" },
  { label: "FAQ", to: "/visit#faq" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(() => parkOpenStatus());
  const { pathname } = useLocation();
  const onAdmin = pathname.startsWith("/admin"); // staff pages open on a cream background, not a dark photo header
  const overHero = !scrolled && !onAdmin;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setInterval(() => setStatus(parkOpenStatus()), 60000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // hide the floating music & WhatsApp widgets while the phone drawer is open so they never cover its links
  useEffect(() => {
    document.body.classList.toggle("drawer-open", open);
    return () => document.body.classList.remove("drawer-open");
  }, [open]);

  const linkTone = (isActive) =>
    isActive ? "text-[#c87700]" : overHero ? "text-white" : "text-[#241c0d]";

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-[#f8b818] px-4 py-1.5 text-[11px] sm:text-xs" data-testid="header-banner-save10">
        <div className="mx-auto flex max-w-7xl items-center">
          <div className="relative min-w-0 flex-1 overflow-hidden" data-testid="banner-marquee">
            <div className="banner-slide w-full">
              <p className="inline-block whitespace-nowrap font-extrabold uppercase tracking-[0.18em] text-[#0a0a0b]">
                Save 10% when you buy online!
              </p>
            </div>
          </div>
          <div className="ml-3 hidden shrink-0 items-center gap-4 font-extrabold uppercase tracking-widest sm:flex">
            <a
              href={PHONE_HREF}
              className="flex items-center gap-1 font-black text-[#0a0a0b] transition-colors hover:text-[#0a0a0b]/80"
              data-testid="nav-secondary-call"
            >
              <Phone size={11} /> {PHONE}
            </a>
          </div>
        </div>
      </div>
      <nav
        className={`relative z-50 transition-all duration-300 ${scrolled ? "glass shadow-[0_10px_40px_rgba(0,0,0,0.2)]" : "bg-transparent"}`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 xl:max-w-[1420px]">
          <div className="flex shrink-0 items-center gap-3">
            <Link to="/" className="flex shrink-0 items-center [perspective:800px]" data-testid="nav-logo-btn" aria-label="Cool Runnings home">
              <img
                src="/logo.png"
                alt="Cool Runnings Cape Town logo"
                className="logo-turn h-28 w-auto max-w-none shrink-0 drop-shadow-[0_6px_0_rgba(9,9,11,0.55)] sm:h-[136px]"
              />
            </Link>
            <Link
              to="/times#schedule"
              title={status.label}
              aria-label={`Park status: ${status.label}`}
              className="relative hidden shrink-0 flex-col items-center xl:flex"
              data-testid="open-status-chip"
            >
              {/* the park's robot — green when open, red when closed (official artwork) — same size as the 3D logo */}
              <img
                src={status.open ? "/traffic-light-green.png" : "/traffic-light-red.png"}
                alt={status.open ? "Traffic light on green — the park is open" : "Traffic light on red — the park is closed"}
                className="h-28 w-auto drop-shadow-[0_6px_0_rgba(9,9,11,0.55)] sm:h-[136px]"
                data-testid="nav-robot-image"
              />
            </Link>
          </div>

          <div className="hidden min-w-0 flex-1 items-center justify-center gap-1 px-6 xl:flex">
            {LINKS.filter((l) => l.label !== "Prices").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-1.5 py-2 text-[13px] font-extrabold tracking-normal transition-colors hover:bg-[#f8b818]/15 hover:text-[#c87700] 2xl:px-2 2xl:text-[15px] ${linkTone(isActive)}`
                }
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "").replace(/[^a-z]/gi, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <Link
              to="/pricing"
              className="hidden shrink-0 items-center rounded-full bg-[#d63a2f] px-4 py-2 text-[13px] font-extrabold uppercase tracking-widest text-white shadow-[0_6px_18px_rgba(214,58,47,0.4)] transition-all hover:-translate-y-0.5 hover:bg-[#b92e25] sm:inline-flex"
              data-testid="nav-prices-btn"
            >
              Prices
            </Link>
            <a
              href={PALISIS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-bypass-overlay="true"
              className="btn-gold hidden shrink-0 whitespace-nowrap !px-5 !py-2.5 sm:inline-flex"
              data-testid="nav-buy-ticket-btn"
            >
              Buy Tickets
            </a>
            <button
              className="rounded-full bg-[#f8b818] p-2.5 text-[#0a0a0b] shadow-[0_6px_20px_rgba(248,184,24,0.45)] transition-transform hover:scale-105 xl:hidden"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="nav-menu-toggle-btn"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-0 z-40 flex h-screen flex-col bg-[#fff7e8] shadow-[0_30px_80px_rgba(36,28,13,0.25)]"
            style={{ height: "100dvh" }}
            data-testid="nav-mobile-menu"
          >
            {/* clear the banner + big 3D logo so no link ever sits or slides behind them */}
            <div className="h-[172px] shrink-0 sm:h-[196px]" />
            <div className="flex-1 overflow-y-auto px-8 pb-10">
              <div className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="py-2.5 text-left text-[26px] leading-tight text-[#0a0a0b] transition-colors hover:text-[#c87700]"
                    style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}
                    data-testid={`mobile-link-${l.to.replace("/", "")}`}
                  >
                    {l.label}
                  </Link>
                ))}
                <a
                  href={PALISIS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-bypass-overlay="true"
                  className="btn-gold mt-6 self-start"
                  data-testid="mobile-buy-ticket-btn"
                >
                  Buy Online &amp; Save 10%
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
